import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import { SportsEsports as SportsEsportsIcon, ArrowBack as ArrowBackIcon, Edit as EditIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../api';
import { toast } from 'react-toastify';
import { adminHasPermission } from '../../../utils/adminPermissions';

const GameTopicView = () => {
  const navigate = useNavigate();
  const canUpdateGameTopics = adminHasPermission('update-game-topics');
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`admin/game-topics/${id}`)
      .then((r) => {
        if (r.data?.success && r.data?.data) setData(r.data.data);
        else setData(null);
      })
      .catch(() => {
        toast.error('Failed to load game topic');
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Game topic not found.</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/game-topics')} sx={{ mt: 2 }}>
          Back to list
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/game-topics')}>
          Back
        </Button>
        {canUpdateGameTopics && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/admin/game-topics/${id}/edit`)}
            sx={{ bgcolor: '#5e35b1', '&:hover': { bgcolor: '#4527a0' } }}
          >
            Edit
          </Button>
        )}
        <Box sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 2,
          ml: 'auto',
          background: 'linear-gradient(135deg, #5e35b1 0%, #7e57c2 100%)',
          borderRadius: '25px',
          px: 3,
          py: 1,
          boxShadow: '0 4px 15px rgba(94, 53, 177, 0.3)',
        }}>
          <SportsEsportsIcon sx={{ fontSize: '24px', color: 'white' }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
            Game Topic Details
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">Subject</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>{data.subject_name || '—'}</Typography>

        <Typography variant="subtitle2" color="text.secondary">Topic</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>{data.topic_name || '—'}</Typography>

        {data.topic_slug && (
          <>
            <Typography variant="subtitle2" color="text.secondary">Topic key (slug)</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>{data.topic_slug}</Typography>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Games ({data.games?.length ?? 0})</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {(data.games || []).length === 0 ? (
            <Typography color="text.secondary">No games assigned</Typography>
          ) : (
            (data.games || []).map((g) => (
              <Chip key={g} label={g} size="small" variant="outlined" />
            ))
          )}
        </Box>

        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Created: {data.created_at ? new Date(data.created_at).toLocaleString() : '—'} · 
          Updated: {data.updated_at ? new Date(data.updated_at).toLocaleString() : '—'}
        </Typography>
      </Paper>
    </Box>
  );
};

export default GameTopicView;
